import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { EDebuggableModuleState, IDebbugable, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { Fragment } from '@fragment/Fragment';
import { Logger, type TLogger } from '@logger/Logger';
import { ResolutionsManagerEvents } from '@resolutionsManager/ResolutionsManager.events';
import { BasePlaylistRotationControllerEvents } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController.events';
import { IFragmentLoadingErrorPayload } from '@streamLoader/FragmentLoader/FragmentLoader.events';
import { StreamLoaderModuleEvents } from '@streamLoader/StreamLoader.events';
import bind from '@utils/decorators/bind';

import { FragmentsQueueEvents } from './FragmentsQueue.events';
import { EIndependenceType } from './IFragmentsQueue';

export class FragmentsQueue implements IDebbugable {
  public _eventBus: TEventBus;
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public groups: string[] = ['queue'];

  private _queuedFragments: Fragment[] = [];
  private _additionalInfo: TDebuggableAdditionalInfo = {};

  private readonly _fragmentRequestedListenerToken: string;
  private readonly _fragmentLoadedListenerToken: string;
  private readonly _fragmentAbortedListenerToken: string;
  private readonly _fragmentErrorListenerToken: string;
  private readonly _softResetRequestedListenerToken: string;
  private readonly _onFragmentsDetectedListenerToken: string;
  private readonly _onFragmentReleaseFromCacheRequestedToken: string;
  private readonly _onResolutionChangedListenerToken: string;

  private readonly _logger: TLogger;

  constructor(eventBus: TEventBus) {
    this._eventBus = eventBus;
    this._logger = Logger(eventBus, 'Doppio - FragmentsQueue');
    this._fragmentRequestedListenerToken = this._eventBus.subscribe(
      StreamLoaderModuleEvents.fragmentLoader.fragments.fragmentLoadingStarted,
      this._onFragmentRequested,
    );

    this._onFragmentsDetectedListenerToken = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.fragments.fragmentsDetected,
      this._onFragmentsDetected,
    );

    this._softResetRequestedListenerToken = this._eventBus.subscribe(
      BufferControllerEvents.requestResetWithOverplay,
      this._onSoftResetRequested,
    );

    this._fragmentLoadedListenerToken = this._eventBus.subscribe(
      StreamLoaderModuleEvents.fragmentLoader.fragments.fragmentLoaded,
      this._onFragmentLoaded,
    );

    this._fragmentAbortedListenerToken = this._eventBus.subscribe(
      StreamLoaderModuleEvents.fragmentLoader.fragments.fragmentLoadingAborted,
      this._onFragmentAborted,
    );

    this._fragmentErrorListenerToken = this._eventBus.subscribe(
      StreamLoaderModuleEvents.fragmentLoader.fragments.fragmentLoadingError,
      this._onFragmentAborted,
    );

    this._onFragmentReleaseFromCacheRequestedToken = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.fragments.requestReleaseFromCache,
      this._onFragmentReleasedFromCache,
    );

    this._onResolutionChangedListenerToken = this._eventBus.subscribe(
      ResolutionsManagerEvents.resolutionChanged,
      this._onSoftResetRequested,
    );
  }

  public getCurrentQueue(): Fragment[] {
    return this._queuedFragments;
  }

  destroy(): void {
    this._eventBus.unsubscribe(this._onFragmentsDetectedListenerToken);
    this._eventBus.unsubscribe(this._fragmentLoadedListenerToken);
    this._eventBus.unsubscribe(this._fragmentRequestedListenerToken);
    this._eventBus.unsubscribe(this._softResetRequestedListenerToken);
    this._eventBus.unsubscribe(this._onResolutionChangedListenerToken);
    this._eventBus.unsubscribe(this._fragmentErrorListenerToken);
    this._eventBus.unsubscribe(this._fragmentAbortedListenerToken);
    this._eventBus.unsubscribe(this._onFragmentReleaseFromCacheRequestedToken);
    this.reset();
  }

  public reset(): void {
    this._queuedFragments = [];
    this.status = 'Idle';
  }

  @bind
  private _onSoftResetRequested(): void {
    this.reset();
  }

  @bind
  private _onFragmentRequested(fragment: Fragment): void {
    if (this._queuedFragments.find(({ url }) => fragment.url === url)) {
      this._logger.warn(`Same fragment (${fragment.getMsnPn()}) was already in queue! Ignoring it`);
      return;
    }

    this._logger.verbose(`Add fragment ${fragment.getMsnPn()} to queue`);
    fragment.goToLifecycleStage('addedToQueue');
    this._queuedFragments.push(fragment);
    this.status = 'Awaiting fragments';
  }

  @bind
  private _onFragmentLoaded(fragment: Fragment): void {
    const queuedFragment: Fragment | undefined = this._queuedFragments.find(({ msn, pn }) => msn === fragment.msn && pn === fragment.pn);

    if (!queuedFragment) {
      this._logger.warn(`Received fragment ${fragment.getMsnPn()} was not in queue. Pls check it`);

      return;
    }

    this._logger.debug(`Fragment ${fragment.getMsnPn()} was resolved finally`);

    queuedFragment.data = fragment.data;

    this._checkQueue();
  }

  @bind
  private _onFragmentReleasedFromCache({ msn }: { msn: number }): void {
    this._queuedFragments = this._queuedFragments.filter((frag) => frag.msn === msn);

    this._checkQueue();
  }

  @bind
  private _onFragmentsDetected(fragments: Fragment[]): void {
    fragments.forEach((fragment) => {
      const queuedFragment: Fragment | undefined = this._queuedFragments.find(({ msn, pn }) => msn === fragment.msn && pn === fragment.pn);

      if (!queuedFragment) {
        return;
      }

      queuedFragment.independent = fragment.independent;
    });

    this._checkQueue();
  }

  @bind
  private _onFragmentAborted(abortedFragment: IFragmentLoadingErrorPayload): void {
    const outdatedFragments: Fragment[] = this._queuedFragments.filter(({ msn }) => msn <= abortedFragment.msn);

    this._queuedFragments = this._queuedFragments.filter((frag) => !outdatedFragments.includes(frag));

    this._checkQueue();
  }

  private _checkQueue(): void {
    this._logger.verbose('Checking queue');
    if (!this._queuedFragments.length) {
      this._logger.verbose('Queue is empty. Skipping');
      return;
    }

    if (!this._queuedFragments[0].data || this._queuedFragments[0].independent === EIndependenceType.UNKNOWN) {
      this._logger.verbose('First queued fragment is empty. Skipping');
      return;
    }

    const nextEmptyFragmentIndex: number = this._queuedFragments.findIndex(
      (item) => !item.data || item.independent === EIndependenceType.UNKNOWN,
    );

    if (nextEmptyFragmentIndex === -1) {
      this._logger.verbose('Looks like whole queue was resolved');

      this._releaseFragmentsFromQueue(this._queuedFragments);
      return;
    }

    this._logger.verbose(`${nextEmptyFragmentIndex} fragments ready. Releasing them`);
    const fragmentsSlice: Fragment[] = this._queuedFragments.slice(0, nextEmptyFragmentIndex);
    this._releaseFragmentsFromQueue(fragmentsSlice);
  }

  @bind
  private _releaseFragmentsFromQueue(fragments: Fragment[]): void {
    this._logger.verbose(`Release fragment  ${fragments.map((fragment) => fragment.getMsnPn())}from queue`);
    fragments.forEach((frag) => frag.goToLifecycleStage('releasedFromQueue'));
    this._queuedFragments = this._queuedFragments.filter((frag) => !fragments.includes(frag));
    this._eventBus.publish(FragmentsQueueEvents.fragmentsReleased, fragments);

    this.status = this._queuedFragments.length > 0 ? this.status : 'Idle';
  }

  get additionalInfo(): TDebuggableAdditionalInfo {
    return {
      ...this._additionalInfo,
      awaitingFragments: this.getCurrentQueue().map((val) => ({ ...val, data: undefined })),
    };
  }
}
